/**
 * React Query hooks for 施術師毎CSV出力 (SCR-046).
 *
 * - useMassagerCsvSearch: paginated search
 * - useMassagerCsvMassagers: massager dropdown
 * - useMassagerCsvExport: CSV download mutation
 */

import { useCallback, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { CSV_SUCCESS_MESSAGE, CSV_ERROR_MESSAGE } from '@/constants/messages';
import {
  searchMassagerCsv,
  getMassagerCsvMassagers,
  exportMassagerCsv,
} from '@/services/main/massagerCsv';
import type {
  MassagerCsvSearchParams,
  MassagerCsvSearchResponse,
  MassagerCsvMassagersResponse,
  MassagerCsvExportPayload,
  MassagerCsvMassagerItem,
} from '@/pages/main/massagerCsv/types';

// ─── Search ──────────────────────────────────────────────────────────────

export function useMassagerCsvSearch(params: MassagerCsvSearchParams | null) {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const toast = useAppToast();

  const { data, isLoading, isFetching, isError, refetch, error } = useQuery<
    MassagerCsvSearchResponse,
    Error
  >({
    queryKey: [
      'massager-csv',
      'search',
      tenant,
      clinicNumber,
      serviceType,
      params,
    ],
    queryFn: () => searchMassagerCsv(tenant, clinicNumber, params!, serviceType),
    enabled: !!tenant && !!clinicNumber && params !== null,
    placeholderData: prev => prev,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  const responseData = data?.data;
  const rows = responseData?.data ?? [];
  const pagination = responseData?.pagination ?? null;
  const labels = responseData?.labels ?? null;

  return { rows, pagination, labels, isLoading: isLoading || isFetching, isError, refetch };
}

// ─── Massager Dropdown ───────────────────────────────────────────────────

export function useMassagerCsvMassagers(searchVisitmonth?: string | null) {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const toast = useAppToast();

  const { data, isLoading, isError, refetch, error } = useQuery<
    MassagerCsvMassagersResponse,
    Error
  >({
    queryKey: ['massager-csv', 'massagers', tenant, clinicNumber, serviceType, searchVisitmonth],
    queryFn: () => getMassagerCsvMassagers(tenant, clinicNumber, searchVisitmonth!, serviceType),
    enabled: !!tenant && !!clinicNumber && !!searchVisitmonth,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  const massagers: MassagerCsvMassagerItem[] = data?.data?.data ?? [];

  return { massagers, isLoading, isError, refetch };
}

// ─── CSV Export Mutation ─────────────────────────────────────────────────

export function useMassagerCsvExport() {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const toast = useAppToast();

  const { mutateAsync, isPending: isLoading } = useMutation<
    Blob,
    Error,
    MassagerCsvExportPayload
  >({
    mutationFn: payload =>
      exportMassagerCsv(tenant, clinicNumber, payload, serviceType),
    onSuccess: () => {
      toast.success(CSV_SUCCESS_MESSAGE);
    },
    onError: () => {
      toast.error(CSV_ERROR_MESSAGE);
    },
  });

  const triggerExport = useCallback(
    async (payload: MassagerCsvExportPayload) => {
      try {
        const blob = await mutateAsync(payload);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const hosoku = payload.rezept_kbn === '2' ? '医療助成' : '';
        const now = new Date();
        const ymd = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
        a.download = `CSV施術師${hosoku}${ymd}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
      } catch {
        // onError already handles toast
      }
    },
    [mutateAsync]
  );

  return { triggerExport, isLoading };
}
